import React, {useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Col, Dropdown, Row, } from "react-bootstrap";
import ReactSwitch from "react-switch";
import IFormWithConfig from "../../../models/misconfig/FormWithConfig";
import IBromcomProviderConfig, { AttendanceApplicationVersion, IBromcomAttendanceSettings } from "../../../models/misconfig/bromcom/BromcomProviderConfig";
import BiometricFilterEdit from "../biometric-filter-edit";
import IBiometricsFilter from "../../../models/misconfig/BiometricsFilter";
import IMedicalConditionsFilter from "../../../models/misconfig/bromcom/MedicalConditionsFilter";
import AttendanceUkDateInput from "../../../pages/establishments/config/attendance/attendance-uk-date-input";
import { toIsoDateFromUkDate, toUkDateFromIsoDate } from "../../../pages/establishments/config/attendance/attendance-utils";
import MedicalConditionsFilterEdit from "./medical-filter-edit";

interface IBromcomProviderConfigEditProps extends IFormWithConfig {
    showGeneralConfigHeading?: boolean;
}

const applicationVersions = [
    { id: 1, value: 6 },
];

const attendanceApplicationVersions = [
    { id: 1, value: 1 as AttendanceApplicationVersion }
];

const defaultAttendanceApplicationVersion = attendanceApplicationVersions[0].value;

const createDefaultAttendanceSettings = (): IBromcomAttendanceSettings => ({
    Enabled: false,
    LiveDate: null,
    AttendanceApplicationVersion: defaultAttendanceApplicationVersion,
});

const BromcomProviderConfigEdit = forwardRef((props: IBromcomProviderConfigEditProps, ref) => {
    const [BromcomConfig, setBromcomConfig] = useState<IBromcomProviderConfig | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [writebackLiveDateInput, setWritebackLiveDateInput] = useState("");

    const numberRegex = /^[1-9]\d*$/;

    useEffect(() => {
        if (props.configJson) {
            const configObj = JSON.parse(props.configJson) as IBromcomProviderConfig;
            const attendance = configObj.Attendance;
            setBromcomConfig({
                ...configObj,
                Attendance: attendance
                    ? {
                        Enabled: attendance.Enabled ?? false,
                        LiveDate: attendance.Enabled ? attendance.LiveDate ?? null : null,
                        AttendanceApplicationVersion: attendance.AttendanceApplicationVersion ?? defaultAttendanceApplicationVersion,
                    }
                    : undefined,
            });
            setWritebackLiveDateInput(toUkDateFromIsoDate(attendance?.LiveDate ?? ""));
        } else {
            setBromcomConfig({
                SchoolID: 0,
                ApplicationVersion: applicationVersions[0].value,
                Attendance: createDefaultAttendanceSettings(),
                BiometricConsents: {
                    FaceRecognition: [],
                    Fingerprints: []
                },
                MedicalConditionsFilter: {
                    DietaryNeeds: [],
                    DietaryPreferences: []
                }
            });
            setWritebackLiveDateInput("");
        }
    }, [props.configJson]);

    useImperativeHandle(ref, () => ({
        validate: function() : boolean {
            const newErrors: { [key: string]: string } = {};

            if(!BromcomConfig?.SchoolID || BromcomConfig.SchoolID <= 0) {
                newErrors['SchoolID'] = 'Valid School ID is required';
            }
            if(!BromcomConfig?.ApplicationVersion) {
                newErrors['ApplicationVersion'] = 'Application Version is required';
            }
            if(BromcomConfig?.Attendance?.Enabled && !BromcomConfig.Attendance.AttendanceApplicationVersion) {
                newErrors['AttendanceApplicationVersion'] = 'Attendance Application Version is required';
            }

            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        }
    }));    

    const handleChange = (key: string, value: any) => {
        if(BromcomConfig === null) {
            return;
        }

        if(key === 'SchoolID') {
            if(value === '' || !numberRegex.test(value)) {
                const updatedErrors = { ...errors };
                updatedErrors['SchoolID'] = 'School ID must be a positive number';
                setErrors(updatedErrors);
                return;
            }
            value = parseInt(value);
        }

        const updatedConfig = { ...BromcomConfig, [key]: value };
        setBromcomConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    }

    const handleAttendanceChange = (key: keyof IBromcomAttendanceSettings, value: boolean | string | null | AttendanceApplicationVersion) => {
        if(BromcomConfig === null) {
            return;
        }

        const updatedConfig: IBromcomProviderConfig = {
            ...BromcomConfig,
            Attendance: {
                ...(BromcomConfig.Attendance ?? createDefaultAttendanceSettings()),
                [key]: value,
            } as IBromcomAttendanceSettings,
        };
        setBromcomConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    };

    const handleBiometricFilterChange = (filter: IBiometricsFilter) => {
        if(BromcomConfig === null) {
            return;
        }   
        const updatedConfig = { ...BromcomConfig, BiometricConsents: filter };
        setBromcomConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    };

    const handleMedicalConditionsFilterChange = (filter: IMedicalConditionsFilter) => {
        if(BromcomConfig === null) {
            return;
        }   
        const updatedConfig = { ...BromcomConfig, MedicalConditionsFilter: filter };
        setBromcomConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    };

    const handleAttendanceWritebackChange = (enabled: boolean) => {
        if(BromcomConfig === null) {
            return;
        }

        const attendance = BromcomConfig.Attendance ?? createDefaultAttendanceSettings();
        const updatedConfig: IBromcomProviderConfig = {
            ...BromcomConfig,
            Attendance: {
                ...attendance,
                Enabled: enabled,
                LiveDate: enabled ? attendance.LiveDate : null,
                AttendanceApplicationVersion: attendance.AttendanceApplicationVersion ?? defaultAttendanceApplicationVersion,
            },
        };
        if (!enabled) {
            const updatedErrors = { ...errors };
            delete updatedErrors.AttendanceWritebackLiveDate;
            setErrors(updatedErrors);
            setWritebackLiveDateInput("");
        }
        setBromcomConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    };

    const handleWritebackLiveDateChange = (value: string) => {
        setWritebackLiveDateInput(value);

        const isoDate = toIsoDateFromUkDate(value);

        if (value === "" || isoDate) {
            handleAttendanceChange("LiveDate", isoDate ?? null);
        }
    };

    return (
        <React.Fragment>
            <section className="bromcom-provider-config-section" aria-labelledby="bromcom-general-config-heading">
                {props.showGeneralConfigHeading !== false && <h3 id="bromcom-general-config-heading">General Config</h3>}
                <Row className="pb-3">
                    <Col md={props.firstCol} className="pt-2"><b>School ID</b></Col>
                    <Col md={props.secondCol}>
                        <input className={`form-control mt-1 ms-0 w-100 ${(errors.SchoolID) ? 'is-invalid' : ''}`} type="text" value={BromcomConfig?.SchoolID ?? ""} required
                            onChange={e => handleChange('SchoolID', e.target.value)} />
                        {errors['SchoolID'] && <div className="invalid-feedback d-block">{errors['SchoolID']}</div>}
                    </Col>
                </Row>
                <Row className="pb-3">
                    <Col md={props.firstCol} className="pt-2"><b>Application version</b></Col>
                    <Col md={props.secondCol}>
                        <Dropdown className="m-auto" onSelect={(eventKey) => handleChange('ApplicationVersion', eventKey !== null ? Number(eventKey) : null)}>
                            <Dropdown.Toggle className="btn btn-light w-100 d-flex justify-content-between align-items-center border">
                                {applicationVersions.find(v => v.value === BromcomConfig?.ApplicationVersion)?.value || 'Select'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="timer-dropdown w-100">
                                {applicationVersions.map(version => (
                                    <Dropdown.Item key={version.id} eventKey={version.value}>
                                        {version.value}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                         {errors['ApplicationVersion'] && <div className="invalid-feedback d-block">{errors['ApplicationVersion']}</div>}
                    </Col>
                </Row>
            </section>
            <section className="bromcom-provider-config-section" aria-labelledby="bromcom-mis-read-data-heading">
                <h3 id="bromcom-mis-read-data-heading">MIS Read Data</h3>
                <BiometricFilterEdit
                    biometricsFilter={BromcomConfig?.BiometricConsents}
                    setBiometricsFilter={handleBiometricFilterChange}
                    firstCol={props.firstCol}
                    secondCol={props.secondCol}
                />
                <MedicalConditionsFilterEdit
                    medicalConditionsFilter={BromcomConfig?.MedicalConditionsFilter}
                    setMedicalConditionsFilter={handleMedicalConditionsFilterChange}
                    firstCol={props.firstCol}
                    secondCol={props.secondCol}
                />
            </section>
            <section className="bromcom-provider-config-section" aria-labelledby="bromcom-attendance-heading">
                <h3 id="bromcom-attendance-heading">Attendance Config</h3>
                <Row className="pb-3">
                    <Col md={props.firstCol} className="pt-2"><b>Attendance</b></Col>
                    <Col md={props.secondCol} className="pt-1">
                        <div className="d-flex align-items-center gap-2">
                            <ReactSwitch
                                aria-label="Enable attendance writeback"
                                checked={BromcomConfig?.Attendance?.Enabled ?? false}
                                checkedIcon={false}
                                handleDiameter={18}
                                height={22}
                                id="bromcom-attendance-writeback-enabled"
                                offColor="#98A2B3"
                                onChange={handleAttendanceWritebackChange}
                                onColor="#1B56A5"
                                uncheckedIcon={false}
                                width={42}
                            />
                            <span>{BromcomConfig?.Attendance?.Enabled ? "Enabled" : "Disabled"}</span>
                        </div>
                    </Col>
                </Row>
                {BromcomConfig?.Attendance?.Enabled && (
                    <>
                        <Row className="pb-3">
                            <Col md={props.firstCol} className="pt-2"><b>Application version</b></Col>
                            <Col md={props.secondCol}>
                                <Dropdown className="m-auto" onSelect={(eventKey) => handleAttendanceChange('AttendanceApplicationVersion', eventKey !== null ? Number(eventKey) as AttendanceApplicationVersion : defaultAttendanceApplicationVersion)}>
                                    <Dropdown.Toggle className="btn btn-light w-100 d-flex justify-content-between align-items-center border">
                                        {attendanceApplicationVersions.find(v => v.value === BromcomConfig.Attendance?.AttendanceApplicationVersion)?.value || 'Select'}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="timer-dropdown w-100">
                                        {attendanceApplicationVersions.map(version => (
                                            <Dropdown.Item key={version.id} eventKey={version.value}>
                                                {version.value}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                                {errors['AttendanceApplicationVersion'] && <div className="invalid-feedback d-block">{errors['AttendanceApplicationVersion']}</div>}
                            </Col>
                        </Row>
                        <Row className="pb-3">
                            <Col md={props.firstCol} className="pt-2"><b>Live date</b></Col>
                            <Col md={props.secondCol}>
                                <AttendanceUkDateInput
                                    calendarPlacement="above"
                                    id="bromcom-attendance-writeback-live-date"
                                    label="Live date"
                                    onChange={handleWritebackLiveDateChange}
                                    placeholder="dd/mm/yyyy"
                                    value={writebackLiveDateInput}
                                />
                                {errors['AttendanceWritebackLiveDate'] && <div className="invalid-feedback d-block">{errors['AttendanceWritebackLiveDate']}</div>}
                            </Col>
                        </Row>
                    </>
                )}
            </section>
        </React.Fragment>
    );
});

export default BromcomProviderConfigEdit;